/**
 * Shared React-Query client + localStorage-persistence wiring.
 *
 * Sprint 6 of the post-launch overhaul (Backlog #18). The client is
 * created once at module load and re-used across the staff + pax apps.
 * Query cache is mirrored into localStorage so reloading a page (or
 * coming back the next morning) renders the previous result instantly
 * while react-query revalidates in the background.
 *
 * Persistence rules:
 *   - 24-hour TTL — anything older than that is dropped on hydration.
 *   - Per-query opt-out via `meta: { persist: false }` on the query
 *     definition (used for high-volatility GDS calls where stale data
 *     is worse than no data).
 *   - Buster keyed off the build version so a deploy invalidates the
 *     entire cache automatically.
 */
import { QueryClient, type Query } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { APP_VERSION } from '../utils/version';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      /**
       * Default behaviour: persist. Opt out per-query via
       *   `useQuery({ queryKey, queryFn, meta: { persist: false } })`
       */
      meta: { persist: true },
    },
  },
});

// Mount persistence only in the browser — Vitest / SSR don't have
// localStorage and would otherwise blow up at module import time.
if (typeof window !== 'undefined') {
  try {
    const persister = createSyncStoragePersister({
      storage: window.localStorage,
      // Larger keys (BFM result sets can be a few hundred KB) — leave
      // throttle generous so we don't write on every keystroke.
      throttleTime: 1000,
    });

    void persistQueryClient({
      queryClient,
      persister,
      maxAge: 24 * 60 * 60 * 1000, // 24h hard ceiling
      // Bumping `buster` invalidates everyone on deploy. Pinned to the
      // current build version so the cache is automatically nuked when
      // we ship a new release.
      buster: APP_VERSION,
      dehydrateOptions: {
        shouldDehydrateQuery: (query: Query) =>
          // Persist only successful queries that haven't opted out via
          // `meta.persist === false`. Errored queries are skipped so we
          // don't trap users on a half-loaded error state across reloads.
          query.state.status === 'success' &&
          (query.meta?.persist as boolean | undefined) !== false,
      },
    });
  } catch {
    // Quota exceeded / private mode / Safari ITP — fall back to in-memory only.
  }
}
